<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4 fw-bold" style="color: #b03a45;">✏️ {{ $t('expenseEdit.title') }}</h4>

      <form @submit.prevent="handleUpdate">
        <div class="row">
          <!-- Type -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.type') }}</label>
              <select v-model="form.type" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseEdit.form.selectType') }}</option>
                <option value="cash">{{ $t('expenseEdit.form.cash') }}</option>
                <option value="gift">{{ $t('expenseEdit.form.gift') }}</option>
                <option value="transfer">{{ $t('expenseEdit.form.transfer') }}</option>
              </select>
            </div>
          </div>

          <!-- Amount -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.amount') }}</label>
              <input type="number" class="form-control border-0" v-model="form.amount" step="0.01" required />
            </div>
          </div>

          <!-- Date -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.date') }}</label>
              <input type="date" class="form-control border-0" v-model="form.date" required />
            </div>
          </div>

          <!-- Note -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.note') }}</label>
              <input type="text" class="form-control border-0" v-model="form.note" />
            </div>
          </div>

          <!-- Category -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.category') }}</label>
              <select v-model="form.category_id" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseEdit.form.selectCategory') }}</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.category_name }}
                </option>
              </select>
            </div>
          </div>

          <!-- CEO -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.ceo') }}</label>
              <select v-model="form.user_id" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseEdit.form.selectCeo') }}</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.firstname }} {{ user.lastname }}
                </option>
              </select>
            </div>
          </div>

          <!-- Company -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseEdit.form.company') }}</label>
              <select v-model="form.company_id" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseEdit.form.selectCompany') }}</option>
                <option v-for="company in companies" :key="company.id" :value="company.id">
                  {{ company.company_name || company.name || company.english_name || 'Unnamed Company' }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Submit -->
        <div class="text-end mt-4">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ $t('expenseEdit.form.submit') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import axios from '@/api'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const id = route.params.id

const form = ref({
  type: '',
  amount: '',
  date: '',
  note: '',
  category_id: '',
  user_id: '',
  company_id: ''
})

const users = ref([])
const companies = ref([])
const categories = ref([])

const fetchDependencies = async () => {
  try {
    const [usersRes, companiesRes, categoriesRes] = await Promise.all([
      axios.get('/getAllUser'),
      axios.get('/companies'),
      axios.get('/Financial/expense-categories')
    ])

    users.value = usersRes.data.data || []
    categories.value = categoriesRes.data || []
    companies.value = (companiesRes.data || []).map(c => ({
      id: c.id,
      company_name: c.company_name || c.name || c.english_name || 'Unnamed Company'
    }))
  } catch (error) {
    console.error(error)
    toast.error(t('expenseEdit.messages.loadError'))
  }
}

const fetchExpense = async () => {
  try {
    const res = await axios.get('/Financial/expenses')
    const allExpenses = res.data.data || []
    const expense = allExpenses.find(e => e.id == id)

    if (!expense) {
      toast.error(t('expenseEdit.messages.notFound'))
      return
    }

    form.value = {
      type: expense.type,
      amount: expense.amount,
      date: expense.date,
      note: expense.note,
      category_id: expense.category_id,
      user_id: expense.user_id,
      company_id: expense.company_id
    }
  } catch (error) {
    toast.error(t('expenseEdit.messages.loadExpenseError'))
  }
}

const handleUpdate = async () => {
  try {
    await axios.put(`/Financial/expenses/${id}`, form.value)
    toast.success(t('expenseEdit.messages.updateSuccess'))
    router.push('/office-expenses')
  } catch (error) {
    console.error(error)
    toast.error(t('expenseEdit.messages.updateError'))
  }
}

onMounted(async () => {
  await fetchDependencies()
  await fetchExpense()
})
</script>

<style scoped>
.form-label {
  font-weight: bold;
  color: #b03a45;
}
</style>
